import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ItemKind, MeetingItem } from '@/api/meetings';
import type { Envelope } from '@/api/envelopes';
import { HeadedCard } from '@/components/HeadedCard';
import { ItemCreateDialog } from './ItemCreateDialog';
import { ItemRow } from './ItemRow';
import type { ItemAddress } from './itemAddress';

const KINDS: ItemKind[] = ['DECISION', 'COMMITMENT', 'GOAL', 'QUESTION'];

interface PersonOption {
  userId: number;
  displayName: string;
}

interface SessionItemsProps {
  items: MeetingItem[];
  address: ItemAddress;
  canWrite: boolean;
  people: PersonOption[];
  envelopes: Envelope[];
  currency: string | null;
  onChanged: () => void;
}

export function SessionItems({
  items,
  address,
  canWrite,
  people,
  envelopes,
  currency,
  onChanged,
}: SessionItemsProps) {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);

  return (
    <HeadedCard
      title={t('workspace.items')}
      subtitle={t('meetings.session.itemsSub')}
      id="session-items"
      actions={
        canWrite ? (
          <Button size="small" onClick={() => setCreating(true)}>
            {t('items.new')}
          </Button>
        ) : undefined
      }
    >
      {items.length === 0 && <Typography color="text.secondary">{t('items.empty')}</Typography>}
      {KINDS.map((kind) => {
        const rows = items.filter((item) => item.kind === kind);
        if (rows.length === 0) return null;
        return (
          <Box key={kind} sx={{ mb: 2 }}>
            <Typography variant="kindLabel" component="h3">
              {t(`items.kind.${kind}`)}
            </Typography>
            {rows.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                address={address}
                canWrite={canWrite}
                people={people}
                currency={currency}
                envelopes={envelopes}
                onChanged={onChanged}
              />
            ))}
          </Box>
        );
      })}
      {creating && (
        <ItemCreateDialog
          address={address}
          people={people}
          envelopes={envelopes}
          currency={currency}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            onChanged();
          }}
        />
      )}
    </HeadedCard>
  );
}
